import { ReactNode, useState } from "react";
import { Stepper, Step, StepLabel, Button, Box } from "@mui/material";
import { styled } from "@mui/system";
import GarbageCanCreationStep from "./GarbageCanCreationStep";
import SummaryStep from "./summaryStep";
import GarbageCanStep from "./garbageCanStep";
import SensorConfigStep from "./sensorConfigStep";
import macAddressUtils from "../../utils/macAddressUtils";
import IGarbageCan from "../../models/garbage can/garbageCan";
import { EGarbageCanType } from "../../models/garbage can/garbageCanType";
import SensorService from "../../services/sensor/SensorService";
import { useTranslation } from "react-i18next";

const CenteredModal = styled(Box)(({ theme }) => ({
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
}));

const ContentBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    width: "50%",
    borderRadius: 5,
    textAlign: "center",
}));

const N_CONFIG_PAGE = 2;
const StepperSensorPage = () => {
    const { t } = useTranslation(["sensor"]);
    const [selectedBuilding, setSelectedBuilding] = useState<string>("");
    const [selectedRoom, setSelectedRoom] = useState<string>("");
    const [selectedFloor, setSelectedFloor] = useState<string>("");

    const [activeStep, setActiveStep] = useState(0);
    const [macAddress, setMacAddress] = useState("");
    const [nGarbagesCan, setNGarbagesCan] = useState<number>(1);
    const [garbageCans, setGarbageCans] = useState<IGarbageCan[]>([
        {
            height: 0,
            length: 0,
            type: EGarbageCanType.Recycling,
            width: 0,
            wiring: 0,
        },
        {
            height: 0,
            length: 0,
            type: EGarbageCanType.Recycling,
            width: 0,
            wiring: 1,
        },
        {
            height: 0,
            length: 0,
            type: EGarbageCanType.Recycling,
            width: 0,
            wiring: 2,
        },
    ]);
    const onChangeGarbageCan = (garbagecan: IGarbageCan, index: number) => {
        if (index < 0) return;
        if (index < nGarbagesCan - 1) {
            garbageCans[index + 1].height = garbagecan.height;
            garbageCans[index + 1].width = garbagecan.width;
            garbageCans[index + 1].length = garbagecan.length;
        }
        garbageCans[index] = garbagecan;
        setGarbageCans([...garbageCans]);
    };

    const onNext = () => {
        if (
            (activeStep === 0 &&
                ((macAddress && macAddress.length === 0) as boolean)) ||
            !macAddressUtils.isMacAdrress(macAddress)
        )
            return;

        if (activeStep <= nGarbagesCan + 1) {
            setActiveStep(activeStep + 1);
        }
    };
    const onBack = () => {
        if (activeStep > 0) setActiveStep(activeStep - 1);
    };
    const onRegister = () => {
        const isValid =
            selectedBuilding !== "" ||
            selectedFloor !== "" ||
            selectedRoom !== "";
        if (!isValid) return;
        SensorService.PostSensors(
            selectedBuilding,
            selectedFloor,
            selectedRoom,
            garbageCans.slice(0, nGarbagesCan)
        );
    };
    const isSummaryStepper = activeStep === nGarbagesCan + N_CONFIG_PAGE;
    const pages: Map<number, ReactNode> = new Map([
        [
            activeStep,
            <GarbageCanCreationStep
                garbageCan={garbageCans[activeStep - N_CONFIG_PAGE]}
                setGarbageCan={(garbageCan: IGarbageCan) =>
                    onChangeGarbageCan(garbageCan, activeStep - N_CONFIG_PAGE)
                }
            />,
        ],
        [
            0,
            <SensorConfigStep
                macAddress={macAddress}
                setMacAddress={setMacAddress}
            />,
        ],
        [
            1,
            <GarbageCanStep
                nGarbagesCan={nGarbagesCan}
                setNGarbagesCan={setNGarbagesCan}
            />,
        ],
        [
            nGarbagesCan + N_CONFIG_PAGE,
            <SummaryStep
                garbageCans={garbageCans}
                macAddress={macAddress}
                nGarbagesCan={nGarbagesCan}
                selectedBuilding={selectedBuilding}
                setSelectedBuilding={setSelectedBuilding}
                selectedRoom={selectedRoom}
                setSelectedRoom={setSelectedRoom}
                selectedFloor={selectedFloor}
                setSelectedFloor={setSelectedFloor}
            />,
        ],
    ]);

    const buttonConfigs = [
        {
            label: t("common:button.back"),
            onClick: onBack,
            isShow: activeStep > 0,
        },
        {
            label: t("common:button.next"),
            onClick: onNext,
            isShow: activeStep < nGarbagesCan + 1,
        },
        {
            label: t("common:button.summary"),
            onClick: onNext,
            isShow: activeStep === nGarbagesCan + 1,
        },
        {
            label: t("common:button.register"),
            onClick: onRegister,
            isShow: isSummaryStepper,
        },
    ];
    const stepConfigs = [
        { label: t("sensor:sensor.configuration") },
        { label: t("sensor:sensor.selectGarbageCan") },
        ...[...Array(nGarbagesCan)].map((_, index) => ({
            label: `${t("sensor:sensor.garbageCan")} ${index + 1}`,
        })),
        { label: t("common:button.summary") },
    ];

    return (
        <CenteredModal>
            <ContentBox>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {stepConfigs.map((step, index) => (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {pages.get(activeStep)}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem", // Adjust the spacing between buttons as needed
                        padding: "0.5rem",
                    }}
                >
                    {buttonConfigs.map(
                        (config, index) =>
                            config.isShow && (
                                <Button
                                    key={index}
                                    variant="contained"
                                    color="primary"
                                    onClick={config.onClick}
                                    sx={{ marginLeft: 1 }}
                                >
                                    {config.label}
                                </Button>
                            )
                    )}
                </Box>
            </ContentBox>
        </CenteredModal>
    );
};
export default StepperSensorPage;
